import React from 'react';

import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import ChecklistIcon from '@mui/icons-material/Checklist';
import {
  Box,
  Chip,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';

import type { OpportunityRelatedInclusion } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { formatDealOptionCurrency, formatDealOptionValue } from './dealOptionsFormatters';
import type { DealOptionRow } from './dealOptionsModel';

export type DealOptionDetailTab = 'inclusions' | 'bundled-experiences';

interface Props {
  activeTab: DealOptionDetailTab;
  onActiveTabChange: (tab: DealOptionDetailTab) => void;
  row: DealOptionRow;
}

const DETAIL_TABS: Array<{ icon: React.ReactElement; label: string; value: DealOptionDetailTab }> = [
  { icon: <ChecklistIcon fontSize="small" />, label: 'Inclusions', value: 'inclusions' },
  { icon: <CardGiftcardIcon fontSize="small" />, label: 'Bundled Experiences', value: 'bundled-experiences' },
];

function getDetailTabCount(tab: DealOptionDetailTab, row: DealOptionRow) {
  if (tab === 'inclusions') {
    return row.inclusions.length;
  }

  return row.bundledExperiences.length;
}

function renderInclusionsPanel(inclusions: Array<OpportunityRelatedInclusion>) {
  if (inclusions.length === 0) {
    return <Typography color="text.secondary">No inclusions are linked to this deal option.</Typography>;
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Inclusion Name</TableCell>
          <TableCell>Inclusion Description</TableCell>
          <TableCell align="right">RRP</TableCell>
          <TableCell>Applies For</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {inclusions.map((inclusion) => (
          <TableRow key={inclusion.id}>
            <TableCell>{formatDealOptionValue(inclusion.name)}</TableCell>
            <TableCell>
              {formatDealOptionValue(inclusion.inclusion_description__c ?? inclusion.description__c)}
            </TableCell>
            <TableCell align="right">{formatDealOptionCurrency('AUD', inclusion.rrp__c)}</TableCell>
            <TableCell>{formatDealOptionValue(inclusion.type__c)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function renderBundledExperiencesPanel(row: DealOptionRow) {
  if (row.bundledExperiences.length === 0) {
    return <Typography color="text.secondary">No bundled experiences are linked to this deal option.</Typography>;
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Listed In</TableCell>
          <TableCell>Account Name</TableCell>
          <TableCell>Deal Option Name</TableCell>
          <TableCell>Fare Type</TableCell>
          <TableCell>Sub Category</TableCell>
          <TableCell align="right">Ancillary Sell price uplift</TableCell>
          <TableCell align="right">Value (cost currency)</TableCell>
          <TableCell>Currency</TableCell>
          <TableCell>Destination</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {row.bundledExperiences.map((experience) => (
          <TableRow key={experience.id}>
            <TableCell>{formatDealOptionValue(experience.listedIn)}</TableCell>
            <TableCell>{formatDealOptionValue(experience.accountName)}</TableCell>
            <TableCell>{formatDealOptionValue(experience.dealOptionName)}</TableCell>
            <TableCell>{formatDealOptionValue(experience.fareType)}</TableCell>
            <TableCell>{formatDealOptionValue(experience.subCategory)}</TableCell>
            <TableCell align="right">{formatDealOptionValue(experience.ancillarySellPriceUplift)}</TableCell>
            <TableCell align="right">
              {formatDealOptionCurrency(experience.currency || row.currency, experience.value)}
            </TableCell>
            <TableCell>{formatDealOptionValue(experience.currency || row.currency)}</TableCell>
            <TableCell>{formatDealOptionValue(experience.destination)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function DealOptionDetails({ activeTab, onActiveTabChange, row }: Props) {
  return (
    <Box sx={{ bgcolor: 'grey.50', px: 3, py: 2 }}>
      <Tabs
        value={activeTab}
        variant="scrollable"
        onChange={(_event, value: DealOptionDetailTab) => onActiveTabChange(value)}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: 40,
          '& .MuiTab-root': { minHeight: 40, textTransform: 'none' },
        }}
      >
        {DETAIL_TABS.map((tab) => (
          <Tab
            key={tab.value}
            icon={tab.icon}
            iconPosition="start"
            label={
              <Stack alignItems="center" direction="row">
                {tab.label}
                <Chip label={getDetailTabCount(tab.value, row)} size="small" sx={{ height: 20, ml: 0.75 }} />
              </Stack>
            }
            value={tab.value}
          />
        ))}
      </Tabs>

      <Box sx={{ pt: 2 }}>
        {activeTab === 'inclusions' && renderInclusionsPanel(row.inclusions)}
        {activeTab === 'bundled-experiences' && renderBundledExperiencesPanel(row)}
      </Box>
    </Box>
  );
}
